import { useDispatch, useSelector } from "react-redux";
import NavigationTop from "../../components/NavigationTop/NavigationTop";
import styles from "./Tutorials.module.css";
import Pagination from "../../components/Pagination/Pagination";
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchTutorials } from "../../store/slices/tutorialsSlice";
import { Helmet } from "react-helmet-async";
import isReactSnap from "../../utils/isReactSnap";
import { resolveMediaUrl } from "../../utils/mediaUrl";

const Tutorials = () => {
  const dispatch = useDispatch();
  const { tutorials, status, error } = useSelector((state) => state.tutorials);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 12;

  // Дефолтные значения page/page_size берутся из fallback выше — не записываем их в URL
  useEffect(() => {
    if (isReactSnap) {
      return;
    }
    dispatch(fetchTutorials({
      page: page,
      page_size: pageSize
    }));
  }, [page, pageSize, dispatch]);


  // Данные для пагинации
  const paginationData = useMemo(() => {
    const totalPages = tutorials?.total_page || 1;
    return {
      count: totalPages * pageSize,
      next: page < totalPages ? page + 1 : null,
      previous: page > 1 ? page - 1 : null,
    };
  }, [tutorials?.total_page, page, pageSize]);

  const shouldShowPagination = (tutorials?.total_page || 1) > 1;

  // Рендер состояния загрузки только при первой загрузке
  if (status === "loading" && !tutorials?.data) {
    return (
      <div>
        <NavigationTop title="Tutorials" />
        <section className="height" style={{ paddingTop: "0" }}>
          <div className={styles.loadingState}>
          </div>
        </section>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div>
        <NavigationTop title="Tutorials" />
        <section className="height" style={{ paddingTop: "0" }}>
          <div className={styles.errorState}>
            <p>Failed to load tutorials. Please try again later.</p>
            <button
              onClick={() => dispatch(fetchTutorials({ page, page_size: pageSize }))}
              className={styles.retryButton}
            >
              Retry
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Distortion Grids Database – Tutorials</title>
        <meta
          name="description"
          content="Step-by-step videos show you of how to use lens data into your preferable workflow."
        />
        <meta property="og:title" content="Distortion Grids Database – Tutorials" />
        <meta
          property="og:description"
          content="Step-by-step videos show you of how to use lens data into your preferable workflow."
        />
      </Helmet>
      <NavigationTop title="Tutorials" />
      <section className={styles.tutorials_content} style={{ paddingTop: "0" }}>
        {/* Верхняя пагинация */}
        {shouldShowPagination && (
          <Pagination pagination={paginationData} currentPage={page} onPageChange={setPage} customPageSize={pageSize} />
        )}

        <div className={styles.container}>
          {tutorials?.data?.length > 0 ? (
            tutorials.data.map((item, index) => (
              <div
                key={item.id}
                className={styles.element}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <Link to={`/tutorials/${item.slug}`} target="_blank" className={styles.imageLink}>
                  <div className={`${styles.hoveredImage} ${hoveredIndex === index ? styles.visible : styles.hidden}`}>
                    <div className={styles.readMore}>
                      Read More
                      <svg
                        width="27"
                        height="36"
                        viewBox="0 0 27 36"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M16.707 11.9908L22.364 17.6478C22.5515 17.8354 22.6568 18.0897 22.6568 18.3548C22.6568 18.62 22.5515 18.8743 22.364 19.0618L16.707 24.7188C16.5184 24.901 16.2658 25.0018 16.0036 24.9995C15.7414 24.9972 15.4906 24.8921 15.3052 24.7067C15.1198 24.5212 15.0146 24.2704 15.0123 24.0082C15.01 23.746 15.1108 23.4934 15.293 23.3048L19.243 19.3548L6 19.3548C5.73478 19.3548 5.48043 19.2495 5.29289 19.0619C5.10536 18.8744 5 18.6201 5 18.3548C5 18.0896 5.10536 17.8353 5.29289 17.6477C5.48043 17.4602 5.73478 17.3548 6 17.3548L19.243 17.3548L15.293 13.4048C15.1975 13.3126 15.1213 13.2022 15.0689 13.0802C15.0165 12.9582 14.9889 12.827 14.9877 12.6942C14.9866 12.5615 15.0119 12.4298 15.0622 12.3069C15.1125 12.184 15.1867 12.0723 15.2806 11.9784C15.3745 11.8845 15.4861 11.8103 15.609 11.76C15.7319 11.7097 15.8636 11.6844 15.9964 11.6856C16.1292 11.6867 16.2604 11.7143 16.3824 11.7667C16.5044 11.8191 16.6148 11.8953 16.707 11.9908Z" />
                      </svg>
                    </div>
                  </div>
                  <img
                    className={styles.image}
                    src={resolveMediaUrl(item.cover)}
                    alt={`Tutorial: ${item.title}`}
                    loading="lazy"
                  />
                </Link>
                <div className={styles.nameCont}>
                  <Link to={`/tutorials/${item.slug}`} className={styles.titleLink}>
                    <p className={styles.title}>{item.title}</p>
                  </Link>
                </div>
                <p className={styles.description}>{item.short_description}</p>
              </div>
            ))
          ) : (
            status === "succeeded" && (
              <div className={styles.emptyState}>
                <p>No tutorials found</p>
              </div>
            )
          )}
        </div>

        {/* Нижняя пагинация */}
        {shouldShowPagination && (
          <Pagination pagination={paginationData} currentPage={page} onPageChange={setPage} customPageSize={pageSize} />
        )}
      </section>
    </div>
  );
};

export default Tutorials;
